import { createSelector } from 'reselect';
import { List as ListImmutable, Set as SetImmutable } from 'immutable';
import { getCategoryGroupLists } from 'data/categoryGroupLists/categoryGroupListsSelectors';
import { getCategoryGroupsById } from 'data/categoryGroups/categoryGroupsSelectors';
import * as budgetItemsSelectors from 'data/budgetItems/budgetItemsSelectors';
import * as categorySelectors from 'data/categories/selectors';
import { getAccountsById } from 'data/accounts/selectors';
import { mkChartOfAccount } from 'data/chartOfAccounts/types';
import createCachedSelector, { LruCacheObject } from 're-reselect';
import { createFlexEqualSelector } from 'utils/selectorsHelpers';
import { BUDGETS_REDUCER_KEY } from './budgetsReducer';
import { BudgetTreeNodeImmutable, BudgetNodeTypes } from './budgetsTypes';

export const getStore = (state) => state[BUDGETS_REDUCER_KEY];

export const getLoadPending = (state) => getStore(state).loadPending;

export const getIsLoading = (state) => getStore(state).isLoading;

export const getLastSyncDate = (state) => getStore(state).lastSyncDate;

export const getBudgets = (state) => getStore(state).resourcesById;

export const getBudgetById = createSelector(
  getBudgets,
  (state, budgetId) => budgetId,
  (budgets, budgetId) => {
    const budget = budgets.get(budgetId);
    return budget;
  }
);

export function getNegate(categoryGroup, target, actual) {
  let negate = -1;

  const categoryGroupType = (categoryGroup && categoryGroup.type) || 'DYNAMIC';
  switch (categoryGroupType) {
    case 'INCOME':
      negate = 1;
      break;
    case 'EXPENSE':
      negate = -1;
      break;
    case 'DYNAMIC':
    default:
      if (target) {
        negate = target <= 0 ? -1 : +1;
      } else if (actual) {
        negate = actual <= 0 ? -1 : +1;
      }
  }

  return negate;
}

export function nodesForCategories(allCategories, categoryGroup, displayPath, categories, depth, budgetItems) {
  const results = { nodes: [], eeCOAs: [], coas: [] };

  categories.forEach((category) => {

    const categoryId = category.id;
    const coa = mkChartOfAccount({ type: 'CATEGORY', id: categoryId });

    const budgetItemsOtherFound = budgetItems.filter((budgetItem) =>
      budgetItem.type === 'CATEGORY_OTHER' && budgetItem.coa && budgetItem.coa.id === categoryId && budgetItem.coa.type === 'CATEGORY');
    const otherFound = budgetItemsOtherFound && budgetItemsOtherFound.size > 0;

    const budgetItemsEverythingElseFound = budgetItems.filter((budgetItem) =>
      budgetItem.coa && budgetItem.coa.id === categoryId && budgetItem.type === 'COA' && budgetItem.coa.type === 'CATEGORY');
    const eeFound = budgetItemsEverythingElseFound && budgetItemsEverythingElseFound.size > 0;

    const newDisplayPath = displayPath ? [...displayPath, category.name] : [category.name];
    const childDisplayPath = (otherFound || eeFound) ? null : newDisplayPath;

    const childCategories = allCategories.filter((child) => child.parentId === categoryId);

    const childResults = childCategories && childCategories.size > 0
      && nodesForCategories(allCategories, categoryGroup, childDisplayPath, childCategories, depth + 1, budgetItems);

    const childNodes = (childResults && childResults.nodes) || [];
    const hasChildren = childNodes && childNodes.length > 0;

    const childCOAs = (childResults && childResults.coas) || [];
    let childEECOAs = (childResults && childResults.eeCOAs) || [];

    const displayLabel = newDisplayPath.join(':');
    if (otherFound || eeFound) {

      const calculationsOther = budgetItemsOtherFound.reduce((accumulator, budgetItem) => ({
        targetAmount: accumulator.targetAmount + budgetItem.amount,
        actualAmount: accumulator.actualAmount + budgetItem.calculatedActualsAmount,
        rolloverAmount: accumulator.rolloverAmount + (budgetItem.rolloverOverrideAmount === undefined ? budgetItem.calculatedRolloverAmount : budgetItem.rolloverOverrideAmount),
      }), {
        targetAmount: 0,
        actualAmount: 0,
        rolloverAmount: 0,
      });

      const calculationsEE = budgetItemsEverythingElseFound.reduce((accumulator, budgetItem) => ({
        targetAmount: accumulator.targetAmount + budgetItem.amount,
        actualAmount: accumulator.actualAmount + budgetItem.calculatedActualsAmount,
        rolloverAmount: accumulator.rolloverAmount + (budgetItem.rolloverOverrideAmount === undefined ? budgetItem.calculatedRolloverAmount : budgetItem.rolloverOverrideAmount),
      }), {
        targetAmount: 0,
        actualAmount: 0,
        rolloverAmount: 0,
      });

      if (hasChildren || (otherFound && eeFound)) {

        if (otherFound) {

          childCOAs.push(coa);

          const childNode = new BudgetTreeNodeImmutable({
            key: `${BudgetNodeTypes.CATEGORY_OTHER}:${categoryId}`,
            type: BudgetNodeTypes.CATEGORY_OTHER,
            id: categoryId,
            coas: new SetImmutable([coa]),
            displayLabel,
            targetAmount: calculationsOther.targetAmount,
            actualAmount: calculationsOther.actualAmount,
            rolloverAmount: calculationsOther.rolloverAmount,
            negate: category.type === 'INCOME' ? +1 : -1,
            depth: depth + 1,
            // parent: ???
            budgetItems: budgetItemsOtherFound,
          });
          childNodes.unshift(childNode);
        } else {

          childEECOAs.push(coa);
        }

        if (eeFound) {

          const childNode = new BudgetTreeNodeImmutable({
            key: `${BudgetNodeTypes.CATEGORY_EE}:${categoryId}`,
            type: BudgetNodeTypes.CATEGORY_EE,
            id: categoryId,
            coas: childEECOAs ? new SetImmutable(childEECOAs) : null,
            displayLabel: 'Everything Else', // `${displayLabel} (everything else)`,
            isEverythingElse: true,
            targetAmount: calculationsEE.targetAmount,
            actualAmount: calculationsEE.actualAmount,
            rolloverAmount: calculationsEE.rolloverAmount,
            negate: category.type === 'INCOME' ? +1 : -1,
            depth: depth + 1,
            budgetItems: budgetItemsEverythingElseFound,
          });
          childNodes.push(childNode);

          if (childEECOAs && childEECOAs.length) {
            childCOAs.push(...childEECOAs);
            childEECOAs = null; // stop propagation
          }
        }

        const calculations = childNodes.reduce((accumulator, node) => ({
          targetAmount: accumulator.targetAmount + node.targetAmount,
          actualAmount: accumulator.actualAmount + node.actualAmount,
          rolloverAmount: accumulator.rolloverAmount + node.rolloverAmount,
        }), {
          targetAmount: 0,
          actualAmount: 0,
          rolloverAmount: 0,
        });

        const node = new BudgetTreeNodeImmutable({
          key: `${BudgetNodeTypes.CATEGORY_ROLL_UP}:${categoryId}`,
          type: BudgetNodeTypes.CATEGORY_ROLL_UP,
          id: categoryId,
          coas: childCOAs ? new SetImmutable(childCOAs) : null,
          displayLabel, // : `${displayLabel} (total)`,
          targetAmount: calculations.targetAmount,
          actualAmount: calculations.actualAmount,
          rolloverAmount: calculations.rolloverAmount,
          negate: category.type === 'INCOME' ? +1 : -1,
          depth,
          children: childNodes && childNodes.length > 0 ? new ListImmutable(childNodes) : null,
        });
        results.nodes.push(node);

      } else {

        const coas = [];
        if (categoryGroup && categoryGroup.coas
          && categoryGroup.coas.find((categoryGroupCOA) => categoryGroupCOA.type === coa.type && categoryGroupCOA.id === coa.id)) {
          coas.push(coa);
        }

        if (eeFound && childEECOAs && childEECOAs.length > 0) {
          coas.push(...childEECOAs);
          childEECOAs = null; // stop propagation
        }
        results.coas.push(...coas);

        const type = (otherFound && BudgetNodeTypes.CATEGORY_OTHER) || (eeFound && BudgetNodeTypes.CATEGORY_EE);
        const node = new BudgetTreeNodeImmutable({
          key: `${type}:${categoryId}`,
          type,
          id: categoryId,
          coas: new SetImmutable(coas),
          displayLabel, // : eeFound ? `${displayLabel} (total)` : displayLabel,
          targetAmount: (otherFound && calculationsOther.targetAmount) || (eeFound && calculationsEE.targetAmount),
          actualAmount: (otherFound && calculationsOther.actualAmount) || (eeFound && calculationsEE.actualAmount),
          rolloverAmount: (otherFound && calculationsOther.rolloverAmount) || (eeFound && calculationsEE.rolloverAmount),
          negate: category.type === 'INCOME' ? +1 : -1,
          depth,
          budgetItems: (otherFound && budgetItemsOtherFound) || (eeFound && budgetItemsEverythingElseFound),
        });
        results.nodes.push(node);
      }

    } else {

      if (categoryGroup && categoryGroup.coas
        && categoryGroup.coas.find((categoryGroupCOA) => categoryGroupCOA.type === coa.type && categoryGroupCOA.id === coa.id)) {
        results.eeCOAs.push(coa);
      }

      if (childNodes && childNodes.length) {
        results.nodes.push(...childNodes);
      }
    }

    if (childCOAs && childCOAs.length) {
      results.coas.push(...childCOAs);
    }

    if (childEECOAs && childEECOAs.length) {
      results.eeCOAs.push(...childEECOAs);
    }

  });

  results.nodes.sort((node1, node2) =>
    node1.displayLabel.localeCompare(
      node2.displayLabel,
      undefined,
      { numeric: true, sensitivity: 'base' },
    ));

  return results;
}

export function nodeForGroupEE(categoryGroup, budgetItems, coas) {
  let groupEENode;

  const budgetItemsFound = budgetItems.filter((budgetItem) => budgetItem.type === 'GROUP');
  if (budgetItemsFound && budgetItemsFound.size > 0) {

    const calculations = budgetItemsFound.reduce((accumulator, budgetItem) => ({
      targetAmount: accumulator.targetAmount + budgetItem.amount,
      actualAmount: accumulator.actualAmount + budgetItem.calculatedActualsAmount,
      rolloverAmount: accumulator.rolloverAmount + (budgetItem.rolloverOverrideAmount === undefined ? budgetItem.calculatedRolloverAmount : budgetItem.rolloverOverrideAmount),
    }), {
      targetAmount: 0,
      actualAmount: 0,
      rolloverAmount: 0,
    });
    const negate = getNegate(categoryGroup, calculations.targetAmount, calculations.actualAmount);

    groupEENode = new BudgetTreeNodeImmutable({
      key: `${BudgetNodeTypes.GROUP_EE}:${categoryGroup.id}`,
      type: BudgetNodeTypes.GROUP_EE,
      id: categoryGroup.id,
      coas,
      displayLabel: `Miscellaneous ${categoryGroup.name}`, // `${categoryGroup.name} (everything else)`,
      isEverythingElse: true,
      targetAmount: calculations.targetAmount,
      actualAmount: calculations.actualAmount,
      rolloverAmount: calculations.rolloverAmount,
      negate,
      depth: 1,
      budgetItems: budgetItemsFound,
    });
  }

  return groupEENode;
}

export function nodesForTransfers(budgetItems, categoryGroup, accountsById) {
  const results = { nodes: [], eeCOAs: [], coas: [] };

  const budgetItemsFound = budgetItems && budgetItems.filter((budgetItem) => budgetItem.type === 'COA' && budgetItem.coa && budgetItem.coa.type === 'ACCOUNT');
  const budgetItemsGrouped = budgetItemsFound && budgetItemsFound.groupBy((budgetItem) => budgetItem.coa.id);
  budgetItemsGrouped.forEach((nodeBudgetItems, accountId) => {
    const calculations = nodeBudgetItems.reduce((accumulator, budgetItem) => ({
      targetAmount: accumulator.targetAmount + budgetItem.amount,
      actualAmount: accumulator.actualAmount + budgetItem.calculatedActualsAmount,
      rolloverAmount: accumulator.rolloverAmount + (budgetItem.rolloverOverrideAmount === undefined ? budgetItem.calculatedRolloverAmount : budgetItem.rolloverOverrideAmount),
    }), {
      targetAmount: 0,
      actualAmount: 0,
      rolloverAmount: 0,
    });
    const coa = mkChartOfAccount({ type: 'ACCOUNT', id: accountId });
    const account = accountsById.get(accountId);
    const negate = getNegate(categoryGroup, calculations.targetAmount, calculations.actualAmount);
    const transferNode = new BudgetTreeNodeImmutable({
      key: `${BudgetNodeTypes.TRANSFER}:${categoryGroup.id}:${accountId}`,
      type: BudgetNodeTypes.TRANSFER,
      id: accountId,
      coas: new SetImmutable([coa]),
      displayLabel: `${negate > 0 ? 'FROM' : 'TO'} ${account ? account.name : '\'unknown account\''}`,
      targetAmount: calculations.targetAmount,
      actualAmount: calculations.actualAmount,
      rolloverAmount: calculations.rolloverAmount,
      negate,
      depth: 1,
      budgetItems: nodeBudgetItems,
    });
    results.nodes.push(transferNode);
    results.coas.push(coa);
  });

  results.eeCOAs = categoryGroup.coas.filter((categoryGroupCOA) => categoryGroupCOA.type === 'ACCOUNT' && !results.coas.find((coa) => coa.id === categoryGroupCOA.id));

  return results;
}

export function nodeForGroup(categoryGroup, childNodes) {

  const calculations = childNodes.reduce((accumulator, node) => ({
    targetAmount: accumulator.targetAmount + node.targetAmount,
    actualAmount: accumulator.actualAmount + node.actualAmount,
    rolloverAmount: accumulator.rolloverAmount + node.rolloverAmount,
    coas: new SetImmutable([...accumulator.coas, ...node.coas]),
  }), {
    targetAmount: 0,
    actualAmount: 0,
    rolloverAmount: 0,
    coas: [],
  });
  const negate = getNegate(categoryGroup, calculations.targetAmount, calculations.actualAmount);

  const groupNode = new BudgetTreeNodeImmutable({
    key: `${BudgetNodeTypes.GROUP}:${categoryGroup.id}`,
    type: BudgetNodeTypes.GROUP,
    id: categoryGroup.id,
    coas: calculations.coas ? new SetImmutable(calculations.coas) : null,
    displayLabel: categoryGroup.name,
    targetAmount: calculations.targetAmount,
    actualAmount: calculations.actualAmount,
    rolloverAmount: calculations.rolloverAmount,
    negate,
    depth: 0,
    children: new ListImmutable(childNodes),
  });

  return groupNode;
}

export const getBudgetTreeNodes = createCachedSelector(
  (state) => ({ obj: getAccountsById(state), deep: false }),
  (state, props) => ({
    obj: budgetItemsSelectors.getBudgetItemsFiltered(state, {
      budget: props.budget,
      startDate: props.startDate,
      endDate: props.endDate,
    }),
    deep: false,
  }),
  (state) => ({ obj: getCategoryGroupLists(state), deep: false }),
  (state) => ({ obj: getCategoryGroupsById(state), deep: false }),
  (state) => ({ obj: categorySelectors.getCategoriesById(state), deep: false }),
  (state, props) => ({ obj: props.budget, deep: false }),
  (state, props) => ({ obj: props.startDate, deep: true }),
  (state, props) => ({ obj: props.endDate, deep: true }),
  (
    { obj: accountsById },
    { obj: budgetItemsById },
    { obj: categoryGroupListsById },
    { obj: categoryGroupsById },
    { obj: categoriesById },
    { obj: budget },
  ) => (new ListImmutable()).withMutations((nodes) => {
    if (budget && budget.catGroupListId) {

      const budgetCategoryGroupList = categoryGroupListsById.get(budget.catGroupListId);
      if (budgetCategoryGroupList && budgetCategoryGroupList.id) {
        let categoryGroups = categoryGroupsById.filter((categoryGroup) => categoryGroup.categoryGroupListId === budgetCategoryGroupList.id).toOrderedMap();
        categoryGroups = categoryGroups.sort((obj1, obj2) =>
          obj1.name.localeCompare(
            obj2.name,
            undefined,
            { numeric: true, sensitivity: 'base' },
          ));

        categoryGroups.forEach((categoryGroup) => {

          const categoryGroupId = categoryGroup.id;
          const budgetItems = budgetItemsById.filter((budgetItem) =>
            (budgetItem.groupId && budgetItem.groupId === categoryGroupId)
            || (categoryGroup.coas && budgetItem.coa && categoryGroup.coas.indexOf(budgetItem.coa) >= 0));

          if (budgetItems.size > 0 && categoryGroup && categoryGroup.coas) {
            // categories that belongs to the group
            const categoryLeaves = categorySelectors.filterCategoriesByGroup(categoriesById, categoryGroup);
            // categories for the tree
            const categoryTree = categorySelectors.filterCategoryTreeByLeaves(categoriesById, categoryLeaves);
            // root categories
            const rootCategories = categoryTree && categoryTree.filter((category) => category.parentId === '0');
            // build category nodes tree
            const categoryResults = nodesForCategories(categoryTree, categoryGroup, null, rootCategories, 1, budgetItems);
            const categoryNodes = categoryResults.nodes;
            // add transfer nodes
            const transferResults = nodesForTransfers(budgetItems, categoryGroup, accountsById);
            const transferNodes = transferResults.nodes;
            // group level ee
            const eeCOAs = new SetImmutable([...categoryResults.eeCOAs, ...transferResults.eeCOAs]);
            const groupEENode = nodeForGroupEE(categoryGroup, budgetItems, eeCOAs);
            // add group node
            if ((categoryNodes && categoryNodes.length > 0)
              || (transferNodes && transferNodes.length > 0)
              || groupEENode
            ) {
              const groupChildNodes = [];
              if (categoryNodes && categoryNodes.length > 0) {
                groupChildNodes.push(...categoryNodes);
              }
              if (transferNodes && transferNodes.length > 0) {
                groupChildNodes.push(...transferNodes);
              }
              if (groupEENode) {
                groupChildNodes.push(groupEENode);
              }
              const groupNode = nodeForGroup(categoryGroup, groupChildNodes);
              if (groupNode) {
                nodes.push(groupNode);
              }
            }
          }
        });
      }
    }
  }),
)(
  (_state, props) => `${props.budget && props.budget.id}:${props.startDate}:${props.endDate}`,
  {
    cacheObject: new LruCacheObject({ cacheSize: 13 }),
    selectorCreator: createFlexEqualSelector,
  },
);

export function pathToNodeWithKeyRecursive(nodes, key) {
  let pathFound;

  if (nodes && nodes.size > 0) {
    nodes.find((node) => {
      if (node.key === key) {
        pathFound = [node];
      } else if (node.children && node.children.size > 0) {
        pathFound = pathToNodeWithKeyRecursive(node.children, key);
        pathFound = pathFound ? [node, ...pathFound] : undefined;
      }
      return pathFound;
    });
  }

  return pathFound;
}

export const pathToNodeWithKey = createSelector(
  (nodes, _key) => nodes,
  (_nodes, key) => key,
  (nodes, key) => pathToNodeWithKeyRecursive(nodes, key)
);

export const coasForNodes = createCachedSelector(
  (nodes) => nodes,
  (nodes) => new SetImmutable(nodes && nodes.reduce((coasArray, node) => node.coas ? [...coasArray, ...node.coas] : coasArray, []))
)(
  (nodes) => `${nodes && nodes.hashCode()}`,
  { cacheObject: new LruCacheObject({ cacheSize: 8 }) },
);
